import { z } from "zod";
import type { LoadBalancerType, RegionPrice } from "./catalogue.js";
import { LabelSelector } from "./firewalls.js";
import { isIpv6, parseIpv4 } from "./ip.js";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/*
 * Load balancers: one address (IPv4 and IPv6) in front of several servers.
 * Each service listens on a port and forwards to a port of the targets
 * ("443 → 8443"); targets are servers, label selectors or IP addresses,
 * reached over their public or their private address. The balancer checks
 * every target and sends traffic only to healthy ones. Billed by the hour
 * up to a monthly cap, by type and location.
 */

export const LoadBalancerName = z.string().trim().min(1).max(128);

export const LoadBalancerAlgorithm = z.enum(["round_robin", "least_connections"]);
export type LoadBalancerAlgorithm = z.infer<typeof LoadBalancerAlgorithm>;

/** `tcp` forwards connections as they are; `http` and `https` read the requests. */
export const LoadBalancerProtocol = z.enum(["tcp", "http", "https"]);
export type LoadBalancerProtocol = z.infer<typeof LoadBalancerProtocol>;

export const TargetHealth = z.enum(["healthy", "unhealthy", "unknown"]);
export type TargetHealth = z.infer<typeof TargetHealth>;

/** A port of a service or its health check, 1–65535. */
export const ServicePort = z.number().int().min(1).max(65_535);

/** Ports each protocol listens on and forwards to unless told otherwise. */
export const DEFAULT_PORTS: Record<LoadBalancerProtocol, { listen: number; destination: number }> =
  {
    tcp: { listen: 443, destination: 443 },
    http: { listen: 80, destination: 80 },
    https: { listen: 443, destination: 80 },
  };

/** Status codes a health check accepts: `200`, or with `?` wildcards such as `2??`. */
export const StatusCodePattern = /^[1-5][0-9?]{2}$/;

export const HealthCheckHttp = z.object({
  /** Host header sent with the check; null sends none. */
  domain: z.string().trim().max(255).nullable(),
  path: z.string().trim().min(1).max(255).startsWith("/"),
  /** Text the answer must contain; null accepts any body. */
  response: z.string().max(255).nullable(),
  statusCodes: z.array(z.string().regex(StatusCodePattern)).min(1).max(20),
  /** Checks over HTTPS (Hetzner's `http.tls`); assumed not to verify the target's certificate. */
  tls: z.boolean(),
});
export type HealthCheckHttp = z.infer<typeof HealthCheckHttp>;

export const HealthCheck = z.object({
  protocol: LoadBalancerProtocol,
  port: ServicePort,
  intervalSeconds: z.number().int().min(1).max(3600),
  timeoutSeconds: z.number().int().min(1).max(3600),
  retries: z.number().int().min(0).max(100),
  /** HTTP options of `http` and `https` checks; null for `tcp`. */
  http: HealthCheckHttp.nullable(),
});
export type HealthCheck = z.infer<typeof HealthCheck>;

/** The check a new service gets: SPEC §6.2 (HTTP, 15 s, 10 s, 3 retries, `/`, `2??,3??`). */
export function defaultHealthCheck(
  protocol: LoadBalancerProtocol,
  destinationPort: number,
): HealthCheck {
  const check = protocol === "tcp" ? "tcp" : "http";
  return {
    protocol: check,
    port: destinationPort,
    intervalSeconds: 15,
    timeoutSeconds: 10,
    retries: 3,
    http:
      check === "tcp"
        ? null
        : { domain: null, path: "/", response: null, statusCodes: ["2??", "3??"], tls: false },
  };
}

/** HTTP options of an `http` or `https` service, as stored (certificates by provider id). */
export const ServiceHttpData = z.object({
  stickySessions: z.boolean(),
  /** Cookie that keeps a client on one target; null for the provider's default. */
  cookieName: z.string().nullable(),
  cookieLifetimeSeconds: z.number().int().nullable(),
  /** Answers plain HTTP on port 80 with a redirect to this HTTPS service. */
  redirectHttp: z.boolean(),
  certificateRefs: z.array(z.string()),
});
export type ServiceHttpData = z.infer<typeof ServiceHttpData>;

/** One service as the provider adapter reports it: "listen on A, forward to B". */
export const LoadBalancerServiceData = z.object({
  protocol: LoadBalancerProtocol,
  listenPort: ServicePort,
  destinationPort: ServicePort,
  /** Prepends the client's address (PROXY protocol); the targets must expect it. */
  proxyProtocol: z.boolean(),
  healthCheck: HealthCheck,
  http: ServiceHttpData.nullable(),
});
export type LoadBalancerServiceData = z.infer<typeof LoadBalancerServiceData>;

/** An object we know by our id and name; id null when the copy does not have it. */
const Ref = z.object({ id: z.uuid().nullable(), name: z.string() });

export const LoadBalancerService = LoadBalancerServiceData.extend({
  /** Certificates of an `https` service (filled in by the API). */
  certificates: z.array(Ref),
});
export type LoadBalancerService = z.infer<typeof LoadBalancerService>;

/** Health of one target per service (by the service's listen port). */
export const TargetHealthEntry = z.object({ listenPort: z.number().int(), status: TargetHealth });
export type TargetHealthEntry = z.infer<typeof TargetHealthEntry>;

/** Targets as the adapter reports them (provider ids of servers). */
export const LoadBalancerTargetData = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("server"),
    serverRef: z.string(),
    usePrivateIp: z.boolean(),
    health: z.array(TargetHealthEntry),
  }),
  z.object({
    type: z.literal("label_selector"),
    selector: z.string(),
    usePrivateIp: z.boolean(),
    /** Servers the selector matches right now, each with its health. */
    matches: z.array(z.object({ serverRef: z.string(), health: z.array(TargetHealthEntry) })),
  }),
  z.object({ type: z.literal("ip"), ip: z.string(), health: z.array(TargetHealthEntry) }),
]);
export type LoadBalancerTargetData = z.infer<typeof LoadBalancerTargetData>;

export const LoadBalancerTarget = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("server"),
    server: Ref,
    serverRef: z.string(),
    usePrivateIp: z.boolean(),
    health: z.array(TargetHealthEntry),
  }),
  z.object({
    type: z.literal("label_selector"),
    selector: z.string(),
    usePrivateIp: z.boolean(),
    servers: z.array(Ref.extend({ serverRef: z.string(), health: z.array(TargetHealthEntry) })),
  }),
  z.object({ type: z.literal("ip"), ip: z.string(), health: z.array(TargetHealthEntry) }),
]);
export type LoadBalancerTarget = z.infer<typeof LoadBalancerTarget>;

/** A load balancer as the provider adapter reports it (without our id). */
export const LoadBalancerData = z.object({
  name: z.string(),
  /** Type code, e.g. `lb11`. */
  type: z.string(),
  region: z.string(),
  zone: z.string(),
  algorithm: LoadBalancerAlgorithm,
  /** Reachable from the Internet; off, it serves only its private networks. */
  publicInterface: z.boolean(),
  ipv4: z.string().nullable(),
  ipv6: z.string().nullable(),
  reverseDns: z.array(z.object({ ip: z.string(), dnsPtr: z.string() })),
  privateNets: z.array(z.object({ networkRef: z.string(), ip: z.string() })),
  services: z.array(LoadBalancerServiceData),
  targets: z.array(LoadBalancerTargetData),
  /** What its type allows. */
  limits: z.object({
    maxServices: z.number().int(),
    maxTargets: z.number().int(),
    maxCertificates: z.number().int(),
    maxConnections: z.number().int(),
  }),
  /** Traffic this month in bytes, when the provider reports it. */
  traffic: z.object({
    inBytes: z.number().nullable(),
    outBytes: z.number().nullable(),
    includedBytes: z.number().nullable(),
  }),
  protection: z.object({ delete: z.boolean() }),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type LoadBalancerData = z.infer<typeof LoadBalancerData>;

export const LoadBalancer = LoadBalancerData.omit({ services: true, targets: true }).extend({
  id: z.uuid(),
  services: z.array(LoadBalancerService),
  targets: z.array(LoadBalancerTarget),
  /** Its private networks by our id and name, with its address in each. */
  networks: z.array(Ref.extend({ networkRef: z.string(), ip: z.string() })),
});
export type LoadBalancer = z.infer<typeof LoadBalancer>;

// --- Requests ---------------------------------------------------------------

export const HealthCheckInput = z.object({
  protocol: LoadBalancerProtocol,
  port: ServicePort,
  intervalSeconds: z.number().int().min(1).max(3600).default(15),
  timeoutSeconds: z.number().int().min(1).max(3600).default(10),
  retries: z.number().int().min(0).max(100).default(3),
  http: HealthCheckHttp.nullable().default(null),
});
export type HealthCheckInput = z.input<typeof HealthCheckInput>;

export const ServiceHttpInput = z.object({
  stickySessions: z.boolean().default(false),
  cookieName: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{1,64}$/, "Letters, digits, - and _ only")
    .nullable()
    .default(null),
  cookieLifetimeSeconds: z.number().int().min(1).max(31_536_000).nullable().default(null),
  redirectHttp: z.boolean().default(false),
  /** Our ids of the certificates an `https` service presents. */
  certificateIds: z.array(z.uuid()).max(50).default([]),
});
export type ServiceHttpInput = z.input<typeof ServiceHttpInput>;

/** A service to add or the new settings of one (its listen port names it). */
export const LoadBalancerServiceInput = z
  .object({
    protocol: LoadBalancerProtocol,
    listenPort: ServicePort,
    destinationPort: ServicePort,
    proxyProtocol: z.boolean().default(false),
    /** Omitted: the default check of the protocol on the destination port. */
    healthCheck: HealthCheckInput.optional(),
    http: ServiceHttpInput.nullable().default(null),
  })
  .superRefine((s, ctx) => {
    if (s.protocol === "tcp" && s.http !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["http"],
        message: "TCP services have no HTTP options",
      });
    }
    if (s.protocol === "https" && (s.http?.certificateIds.length ?? 0) === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["http", "certificateIds"],
        message: "An HTTPS service needs a certificate",
      });
    }
    if (s.protocol !== "https" && s.http?.redirectHttp) {
      ctx.addIssue({
        code: "custom",
        path: ["http", "redirectHttp"],
        message: "Only HTTPS services redirect plain HTTP",
      });
    }
    if (s.protocol === "http" && (s.http?.certificateIds.length ?? 0) > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["http", "certificateIds"],
        message: "Only HTTPS services take certificates",
      });
    }
    const check = s.healthCheck;
    if (check && check.protocol !== "tcp" && check.http === null) {
      ctx.addIssue({
        code: "custom",
        path: ["healthCheck", "http"],
        message: "HTTP checks need a path and status codes",
      });
    }
  });
export type LoadBalancerServiceInput = z.input<typeof LoadBalancerServiceInput>;
export type LoadBalancerServiceRequest = z.output<typeof LoadBalancerServiceInput>;

const TargetIp = z
  .string()
  .trim()
  .refine((v) => parseIpv4(v) !== null || isIpv6(v), "Not an IP address");

export const LoadBalancerTargetInput = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("server"),
    serverId: z.uuid(),
    usePrivateIp: z.boolean().default(false),
  }),
  z.object({
    type: z.literal("label_selector"),
    selector: LabelSelector,
    usePrivateIp: z.boolean().default(false),
  }),
  z.object({ type: z.literal("ip"), ip: TargetIp }),
]);
export type LoadBalancerTargetInput = z.input<typeof LoadBalancerTargetInput>;

/** The target to remove, by what names it. */
export const RemoveLoadBalancerTargetRequest = z.discriminatedUnion("type", [
  z.object({ type: z.literal("server"), serverId: z.uuid() }),
  z.object({ type: z.literal("label_selector"), selector: LabelSelector }),
  z.object({ type: z.literal("ip"), ip: TargetIp }),
]);
export type RemoveLoadBalancerTargetRequest = z.infer<typeof RemoveLoadBalancerTargetRequest>;

const uniquePorts = (services: { listenPort: number }[]) =>
  new Set(services.map((s) => s.listenPort)).size === services.length;

export const CreateLoadBalancerRequest = z
  .object({
    name: LoadBalancerName,
    type: z.string().trim().min(1).max(32),
    region: z.string().trim().min(1).max(64),
    algorithm: LoadBalancerAlgorithm.default("round_robin"),
    publicInterface: z.boolean().default(true),
    /** A private network to join at once; needed when the public interface is off. */
    networkId: z.uuid().optional(),
    services: z.array(LoadBalancerServiceInput).max(50).default([]),
    targets: z.array(LoadBalancerTargetInput).max(150).default([]),
    labels: Labels.default({}),
  })
  .refine((v) => v.publicInterface || v.networkId !== undefined, {
    path: ["networkId"],
    message: "Without the public interface the balancer needs a private network",
  })
  .refine((v) => uniquePorts(v.services), {
    path: ["services"],
    message: "Each service needs its own listen port",
  });
export type CreateLoadBalancerRequest = z.input<typeof CreateLoadBalancerRequest>;

export const UpdateLoadBalancerRequest = z
  .object({ name: LoadBalancerName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateLoadBalancerRequest = z.infer<typeof UpdateLoadBalancerRequest>;

export const ChangeLoadBalancerTypeRequest = z.object({ type: z.string().trim().min(1).max(32) });
export const ChangeLoadBalancerAlgorithmRequest = z.object({ algorithm: LoadBalancerAlgorithm });
export const AttachLoadBalancerNetworkRequest = z.object({
  networkId: z.uuid(),
  /** A fixed address in a subnet of the balancer's zone; the provider picks one when absent. */
  ip: z
    .string()
    .trim()
    .refine((v) => parseIpv4(v) !== null, "Not an IPv4 address")
    .optional(),
});
export const DetachLoadBalancerNetworkRequest = z.object({ networkId: z.uuid() });
export const LoadBalancerPublicInterfaceRequest = z.object({ enabled: z.boolean() });
export const LoadBalancerProtectionRequest = z.object({ delete: z.boolean() });

/** Answer of writes the provider finishes asynchronously. */
export const LoadBalancerChange = z.object({ loadBalancer: LoadBalancer, operation: Operation });
export type LoadBalancerChange = z.infer<typeof LoadBalancerChange>;

// --- Health and prices --------------------------------------------------------

/** Health of a target, or of a whole balancer, summed up over its checks. */
export type HealthSummary = "healthy" | "unhealthy" | "partial" | "unknown" | "none";

/** The health of a set of check results: all healthy, none, some, or not checked yet. */
export function summarizeHealth(statuses: TargetHealth[]): HealthSummary {
  if (statuses.length === 0) return "none";
  if (statuses.every((s) => s === "healthy")) return "healthy";
  if (statuses.every((s) => s === "unhealthy")) return "unhealthy";
  if (statuses.includes("healthy")) return "partial";
  return statuses.includes("unhealthy") ? "unhealthy" : "unknown";
}

/** Every health check result of a target (a label selector: of all servers it matches). */
export const targetStatuses = (target: LoadBalancerTarget): TargetHealth[] =>
  target.type === "label_selector"
    ? target.servers.flatMap((s) => s.health.map((h) => h.status))
    : target.health.map((h) => h.status);

export const targetHealth = (target: LoadBalancerTarget): HealthSummary =>
  summarizeHealth(targetStatuses(target));

/** "none" when there is nothing to check (no targets or no services). */
export const healthSummary = (lb: Pick<LoadBalancer, "targets">): HealthSummary =>
  summarizeHealth(lb.targets.flatMap(targetStatuses));

/** The price of a load balancer of this type in its region; null when not listed. */
export function loadBalancerPrice(
  lb: { type: string; region: string },
  types: Pick<LoadBalancerType, "code" | "prices">[],
): RegionPrice | null {
  return types.find((t) => t.code === lb.type)?.prices.find((p) => p.region === lb.region) ?? null;
}

/**
 * Monthly cap of a load balancer, net of VAT (billed by the hour up to it);
 * null when the catalogue has no price for its type and region. For the
 * project's cost ledger and the dashboard.
 */
export function loadBalancerMonthly(
  lb: { type: string; region: string },
  types: Pick<LoadBalancerType, "code" | "prices">[],
): number | null {
  const price = loadBalancerPrice(lb, types);
  return price ? Number(price.monthly.net) : null;
}

/** Hourly price, net of VAT; null when not listed. */
export function loadBalancerHourly(
  lb: { type: string; region: string },
  types: Pick<LoadBalancerType, "code" | "prices">[],
): number | null {
  const price = loadBalancerPrice(lb, types);
  return price ? Number(price.hourly.net) : null;
}

/** "443 → 8443": how a service forwards, for lists and the dashboard. */
export const serviceText = (s: Pick<LoadBalancerServiceData, "listenPort" | "destinationPort">) =>
  `${s.listenPort} → ${s.destinationPort}`;
